/**
 * 公開同意書画面 (Phase 4)。
 *
 * URL: /consent/:dealId/:linkId
 *
 * フロー:
 *   1. getPublicConsentDocument Callable でスナップショットを取得
 *   2. 本文セクションを順番にレンダリング
 *   3. ロゴ/印鑑を配置 (印鑑は stampHidden=false のときのみ)
 *   4. デジタル署名フォーム (氏名 + 署名日) で入力
 *   5. 「同意して提出」ボタン → submitConsentDocument Callable
 *   6. 提出成功時はサンクス画面に遷移
 *
 * サイン済みの場合 (v3.4+):
 *   - 提出時に凍結されたスナップショットを **同意書の体裁のまま** 表示 (閲覧のみ)
 *   - 同意チェックはチェック済み・入力不可で再現
 *   - 署名欄に署名者 / 署名日 / 提出日時を印字
 *   - 「PDF/印刷」ボタン (window.print → ブラウザの PDF 保存)
 *   - 送信直後はサンクス画面を出し、そこから同意書表示へ切り替えられる
 */
import type { ConsentImagePosition, ConsentSection } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import {
  CheckCircle2,
  FileText,
  Loader2,
  Printer,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';
import { functions } from '@/lib/firebase';

import { ConsentSectionRenderer } from './ConsentSectionRenderer';

interface PublicConsentView {
  status: 'ok' | 'not_found';
  title?: string;
  sections?: ConsentSection[];
  logoStoragePath?: string | null;
  logoPosition?: ConsentImagePosition | null;
  stampStoragePath?: string | null;
  stampPosition?: ConsentImagePosition | null;
  stampHidden?: boolean;
  submitted?: boolean;
  submittedAt?: string | null;
  signatureName?: string | null;
  signatureDate?: string | null;
  consentChecks?: Record<string, boolean> | null;
}

type State =
  /** 読込中 */
  | { kind: 'loading' }
  /** リンク不正 / 削除済み */
  | { kind: 'not_found' }
  /** 未サイン — 署名フォームを表示 */
  | { kind: 'ok'; view: PublicConsentView }
  /** 送信直後 — サンクス画面 (同意書表示ボタン付き) */
  | { kind: 'thanks'; view: PublicConsentView }
  /** サイン済み — 同意書を閲覧のみで表示 */
  | { kind: 'signed'; view: PublicConsentView };

const getPublicFn = httpsCallable<
  { dealId: string; linkId: string },
  PublicConsentView
>(functions, 'getPublicConsentDocument');

const submitFn = httpsCallable<
  {
    dealId: string;
    linkId: string;
    signatureName: string;
    signatureDate: string;
    consentChecks?: Record<string, boolean>;
    clientAudit?: {
      timezone?: string | null;
      language?: string | null;
      screenResolution?: string | null;
      viewportSize?: string | null;
      platform?: string | null;
      referrer?: string | null;
      clientSubmittedAt?: string | null;
      pageLoadDurationMs?: number | null;
    };
  },
  { status: string; submissionId?: string }
>(functions, 'submitConsentDocument');

/** ブラウザ環境のメタデータを収集 (v3.2.27+ 法的証跡)。 */
function collectClientAudit(pageLoadedAtMs: number) {
  const tz = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      return null;
    }
  })();
  const platform = (() => {
    // navigator.userAgentData が無い旧ブラウザは navigator.platform
    const uad = (
      navigator as { userAgentData?: { platform?: string } }
    ).userAgentData;
    if (uad?.platform) return uad.platform;
    return typeof navigator.platform === 'string' ? navigator.platform : null;
  })();
  return {
    timezone: tz,
    language: typeof navigator.language === 'string' ? navigator.language : null,
    screenResolution:
      typeof screen?.width === 'number' && typeof screen?.height === 'number'
        ? `${screen.width}x${screen.height}`
        : null,
    viewportSize:
      typeof window.innerWidth === 'number' && typeof window.innerHeight === 'number'
        ? `${window.innerWidth}x${window.innerHeight}`
        : null,
    platform,
    referrer: typeof document.referrer === 'string' ? document.referrer : null,
    clientSubmittedAt: new Date().toISOString(),
    pageLoadDurationMs: Date.now() - pageLoadedAtMs,
  };
}

/** ISO 文字列を日本語ロケールの日時表記へ。失敗時は空文字。 */
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ja-JP');
}

/** 画像をロゴ/印鑑として配置するコンポーネント。 */
function PositionedImage({
  url,
  position,
  size = 80,
  alt,
}: {
  url: string;
  position: ConsentImagePosition;
  size?: number;
  alt: string;
}) {
  const sizeStyle = { width: size, height: size, objectFit: 'contain' as const };
  const classMap: Record<ConsentImagePosition, string> = {
    'top-left': 'self-start mr-auto',
    'top-center': 'self-center mx-auto',
    'top-right': 'self-end ml-auto',
    'bottom-left': 'self-start mr-auto',
    'bottom-center': 'self-center mx-auto',
    'bottom-right': 'self-end ml-auto',
  };
  return (
    <div className={`flex ${classMap[position]}`}>
      <img src={url} alt={alt} style={sizeStyle} className="rounded" />
    </div>
  );
}

export function PublicConsentPage() {
  usePublicPageMode();
  const { dealId, linkId } = useParams<{ dealId: string; linkId: string }>();

  const [state, setState] = useState<State>({ kind: 'loading' });
  const [signatureName, setSignatureName] = useState('');
  const [signatureDate, setSignatureDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 同意チェック (v3.2.26+) — section.id → ON/OFF */
  const [consentChecks, setConsentChecks] = useState<Record<string, boolean>>({});
  /** ページロード時刻 (v3.2.27+ メタデータ用) */
  const pageLoadedAtMsRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!dealId || !linkId) {
      setState({ kind: 'not_found' });
      return;
    }
    void (async () => {
      try {
        const r = await getPublicFn({ dealId, linkId });
        const view = r.data;
        if (view.status === 'ok') {
          // サイン済みなら最初から「サイン済み同意書」を表示 (v3.4+)
          setState({ kind: view.submitted ? 'signed' : 'ok', view });
        } else {
          setState({ kind: 'not_found' });
        }
      } catch (e) {
        console.error(e);
        setState({ kind: 'not_found' });
      }
    })();
  }, [dealId, linkId]);

  // PDF 保存時のファイル名がブラウザの document.title 由来になるため、
  // サイン済み表示のあいだだけ「タイトル_署名者」に差し替える。
  useEffect(() => {
    if (state.kind !== 'signed') return;
    const prev = document.title;
    const name = state.view.signatureName?.trim();
    document.title = [state.view.title ?? '同意書', name].filter(Boolean).join('_');
    return () => {
      document.title = prev;
    };
  }, [state]);

  // 同意チェックセクションの ID 一覧 (state が ok のときだけ計算)
  const consentCheckIds = useMemo(() => {
    if (state.kind !== 'ok') return [];
    return (state.view.sections ?? [])
      .filter((s) => s.type === 'consent_check')
      .map((s) => s.id);
  }, [state]);

  const allChecksOn =
    consentCheckIds.length === 0 ||
    consentCheckIds.every((id) => consentChecks[id] === true);

  const handleSubmit = async () => {
    if (!dealId || !linkId) return;
    if (state.kind !== 'ok') return;
    if (!signatureName.trim()) {
      setError('氏名を入力してください');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(signatureDate)) {
      setError('署名日を YYYY-MM-DD 形式で入力してください');
      return;
    }
    if (!allChecksOn) {
      setError('すべての同意チェックを ON にしてください');
      return;
    }
    const baseView = state.view;
    setSubmitting(true);
    setError(null);
    try {
      const r = await submitFn({
        dealId,
        linkId,
        signatureName: signatureName.trim(),
        signatureDate,
        consentChecks,
        clientAudit: collectClientAudit(pageLoadedAtMsRef.current),
      });
      if (r.data.status === 'ok' || r.data.status === 'already_submitted') {
        // 送信内容をそのまま「サイン済み」表示の素材にする
        // (サーバ再取得を待たずに同意書を見返せるようにするため)
        setState({
          kind: 'thanks',
          view: {
            ...baseView,
            submitted: true,
            submittedAt: new Date().toISOString(),
            signatureName: signatureName.trim(),
            signatureDate,
            consentChecks,
          },
        });
      } else if (r.data.status === 'invalid') {
        setError('入力内容が不正です。氏名と署名日をご確認ください。');
      } else if (r.data.status === 'consent_missing') {
        setError('すべての同意チェックを ON にしてください');
      } else {
        setError('送信に失敗しました。URL をご確認ください。');
      }
    } catch (e) {
      console.error(e);
      setError(
        `送信に失敗しました: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Spinner />
      </div>
    );
  }

  if (state.kind === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <ShieldCheck className="size-12 text-gray-400 mx-auto mb-2" />
          <h1 className="text-lg font-semibold mb-1">同意書が見つかりません</h1>
          <p className="text-sm text-gray-600">
            URL をご確認のうえ、お手数ですが送信元にお問い合わせください。
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === 'thanks') {
    const thanksView = state.view;
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white p-6"
        style={{ colorScheme: 'light' }}
      >
        <div className="text-center max-w-md">
          <CheckCircle2 className="size-16 text-green-500 mx-auto mb-2" />
          <h1 className="text-xl font-semibold mb-2">サインを受け付けました</h1>
          <p className="text-sm text-gray-600">
            ご署名いただきありがとうございます。
            <br />
            このページは閉じていただいて構いません。
          </p>
          {thanksView.signatureName && (
            <p className="text-xs text-gray-500 mt-3">
              署名者: {thanksView.signatureName}
            </p>
          )}
          <Button
            type="button"
            variant="secondary"
            className="mt-5"
            onClick={() => setState({ kind: 'signed', view: thanksView })}
          >
            <FileText className="size-4 mr-1" />
            サインした同意書を表示
          </Button>
        </div>
      </div>
    );
  }

  const { view } = state;
  const sections = view.sections ?? [];
  const showStamp = !view.stampHidden && view.stampStoragePath;
  const logo = view.logoStoragePath
    ? {
        url: view.logoStoragePath,
        position: view.logoPosition ?? ('top-right' as ConsentImagePosition),
      }
    : null;
  const stamp =
    showStamp && view.stampStoragePath
      ? {
          url: view.stampStoragePath,
          position: view.stampPosition ?? ('bottom-right' as ConsentImagePosition),
        }
      : null;

  // ── サイン済み: 閲覧のみ (同意チェックは提出時の状態で固定) ──────────
  if (state.kind === 'signed') {
    const signedChecks = view.consentChecks ?? {};
    return (
      <PublicConsentLayout
        title={view.title ?? '同意書'}
        logo={logo}
        stamp={stamp}
        sections={sections}
        consentChecks={signedChecks}
        readOnly
        header={
          <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2 rounded border border-green-300 bg-green-50 px-3 py-2">
            <span className="flex items-center gap-1.5 text-sm font-medium text-green-800">
              <CheckCircle2 className="size-4" />
              サイン済みの同意書です
            </span>
            <Button type="button" variant="primary" onClick={() => window.print()}>
              <Printer className="size-4 mr-1" />
              PDF/印刷
            </Button>
          </div>
        }
      >
        <SignedSignatureBlock
          signatureName={view.signatureName ?? ''}
          signatureDate={view.signatureDate ?? ''}
          submittedAt={view.submittedAt ?? null}
        />
      </PublicConsentLayout>
    );
  }

  // ── 未サイン: 署名フォームを表示 (state.kind === 'ok') ──────────────
  return (
    <PublicConsentLayout
      title={view.title ?? '同意書'}
      logo={logo}
      stamp={stamp}
      sections={sections}
      consentChecks={consentChecks}
      onCheckChange={(id, v) =>
        setConsentChecks((prev) => ({ ...prev, [id]: v }))
      }
    >
      <section className="mt-8 border-t border-gray-300 pt-6">
        <h3 className="text-base font-semibold mb-2">署名</h3>
        <p className="text-xs text-gray-600 mb-4">
          上記の内容に同意のうえ、お名前と署名日をご入力ください。
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="sig-name" className="text-sm">
              氏名
            </Label>
            <Input
              id="sig-name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="例: 山田 太郎"
              disabled={submitting}
            />
          </div>
          <div>
            <Label htmlFor="sig-date" className="text-sm">
              署名日
            </Label>
            <Input
              id="sig-date"
              type="date"
              value={signatureDate}
              onChange={(e) => setSignatureDate(e.target.value)}
              disabled={submitting}
            />
          </div>
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}
          {!allChecksOn && consentCheckIds.length > 0 && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              同意チェックがまだ ON になっていません。本文中のチェックボックスをすべて ON にしてから送信してください。
            </p>
          )}
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={submitting || !allChecksOn}
            className="w-full"
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 mr-1 animate-spin" />
                送信中…
              </>
            ) : (
              <>
                <ShieldCheck className="size-4 mr-1" />
                同意して提出
              </>
            )}
          </Button>
        </div>
      </section>
    </PublicConsentLayout>
  );
}

/** サイン済み同意書の署名欄 (入力フォームの代わりに印字する)。 */
function SignedSignatureBlock({
  signatureName,
  signatureDate,
  submittedAt,
}: {
  signatureName: string;
  signatureDate: string;
  submittedAt: string | null;
}) {
  const submittedAtText = fmtDateTime(submittedAt);
  return (
    <section className="mt-8 border-t border-gray-300 pt-6">
      <h3 className="text-base font-semibold mb-3">署名</h3>
      <dl className="space-y-2 text-sm">
        <div className="flex items-baseline gap-3">
          <dt className="w-20 shrink-0 text-xs text-gray-600">署名者</dt>
          <dd className="flex-1 border-b border-gray-400 pb-1 text-base font-medium">
            {signatureName || '—'}
          </dd>
        </div>
        <div className="flex items-baseline gap-3">
          <dt className="w-20 shrink-0 text-xs text-gray-600">署名日</dt>
          <dd className="flex-1 border-b border-gray-400 pb-1">
            {signatureDate || '—'}
          </dd>
        </div>
      </dl>
      {submittedAtText && (
        <p className="mt-3 text-[11px] text-gray-500">
          電子署名により {submittedAtText} に提出されました。
        </p>
      )}
    </section>
  );
}

function PublicConsentLayout({
  title,
  logo,
  stamp,
  sections,
  consentChecks,
  onCheckChange,
  readOnly = false,
  header,
  children,
}: {
  title: string;
  logo: { url: string; position: ConsentImagePosition } | null;
  stamp: { url: string; position: ConsentImagePosition } | null;
  sections: ConsentSection[];
  consentChecks: Record<string, boolean>;
  /** 未指定 = 閲覧のみ (チェックボックスは disabled)。 */
  onCheckChange?: (id: string, v: boolean) => void;
  readOnly?: boolean;
  /** 本文の上に出す帯 (サイン済みバナー + 印刷ボタン等)。 */
  header?: React.ReactNode;
  children: React.ReactNode;
}) {
  // ロゴ位置: top-* は上段、bottom-* は下段
  const { topLogos, bottomLogos } = useMemo(() => {
    const top: Array<{
      url: string;
      position: ConsentImagePosition;
      kind: 'logo' | 'stamp';
    }> = [];
    const bottom: Array<{
      url: string;
      position: ConsentImagePosition;
      kind: 'logo' | 'stamp';
    }> = [];
    if (logo) {
      (logo.position.startsWith('top-') ? top : bottom).push({
        ...logo,
        kind: 'logo',
      });
    }
    if (stamp) {
      (stamp.position.startsWith('top-') ? top : bottom).push({
        ...stamp,
        kind: 'stamp',
      });
    }
    return { topLogos: top, bottomLogos: bottom };
  }, [logo, stamp]);

  return (
    <>
      <style>{`
        @page { size: A4; margin: 15mm; }
        @media print {
          /* 100vh 固定・overflow:hidden のアプリ用レイアウトを印刷時は解除。
             残しておくと内容が切れたり空白ページが生まれる。 */
          html, body, #root {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          body { background: white; }
          .no-print { display: none !important; }
          .consent-page {
            box-shadow: none !important;
            border: 0 !important;
            padding: 0 !important;
          }
          /* チェック状態はグレースケール印刷でも判別できる必要がある */
          .consent-page input[type='checkbox'] {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
      <div
        className="min-h-screen print:min-h-0 bg-gray-50 print:bg-white py-6 print:py-0 px-3 print:px-0 text-gray-900"
        style={{ colorScheme: 'light' }}
      >
        <div className="max-w-2xl mx-auto">
          {header}
          <div className="consent-page bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-10">
            {/* ロゴ・印鑑 (上段) */}
            {topLogos.length > 0 && (
              <div className="flex justify-between items-start mb-4 gap-3">
                {topLogos.map((it, i) => (
                  <PositionedImage
                    key={i}
                    url={it.url}
                    position={it.position}
                    size={it.kind === 'stamp' ? 64 : 80}
                    alt={it.kind === 'stamp' ? '印鑑' : 'ロゴ'}
                  />
                ))}
              </div>
            )}

            <h1 className="text-2xl font-bold text-center mb-6">{title}</h1>

            <div className="prose prose-sm max-w-none">
              {sections.map((sec) => (
                <ConsentSectionRenderer
                  key={sec.id}
                  section={sec}
                  publicTheme
                  checked={
                    sec.type === 'consent_check'
                      ? Boolean(consentChecks[sec.id])
                      : undefined
                  }
                  onCheckChange={
                    sec.type === 'consent_check' && !readOnly
                      ? (v) => onCheckChange?.(sec.id, v)
                      : undefined
                  }
                />
              ))}
            </div>

            {children}

            {/* 印鑑 (下段) */}
            {bottomLogos.length > 0 && (
              <div className="flex justify-between items-end mt-6 gap-3">
                {bottomLogos.map((it, i) => (
                  <PositionedImage
                    key={i}
                    url={it.url}
                    position={it.position}
                    size={it.kind === 'stamp' ? 64 : 80}
                    alt={it.kind === 'stamp' ? '印鑑' : 'ロゴ'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
